/**
 * @bos/portal3 — the apps query/mutation layer.
 *
 * An APP is a thing the business is having built; one business can have several. These
 * are the projects hooks' analogue, same shape, same absence of react-query:
 *
 *   - useProjectApps(projectId) → the apps under one business, newest first, with a
 *     `create` that prepends the new one. The server returns them owner-scoped.
 *   - useApp(id)                → one app plus the name of the business it belongs to,
 *     with a distinct `not-found` status (a deep-linked or someone-else's id renders a
 *     clean not-found page, never a crash).
 *
 * `appNameConflictOf` classifies the one refusal a person can actually cause: naming an
 * app the business already has. That is INFORMATION — here is the app you already have —
 * not an error, so it is separated out the way `startConflictOf` separates the live-run
 * refusal.
 */
import { useCallback, useEffect, useState } from 'react';
import { TRPCClientError } from '@trpc/client';
import type { App } from '@bos/portal3-server';
import { client } from '../rpc/client';

export type { App };

/** Turn a thrown tRPC error into a message safe to render (zod issues → first message). */
export function appErrorMessage(err: unknown): string {
  if (err instanceof TRPCClientError) {
    const raw: string = err.message ?? '';
    try {
      const issues = JSON.parse(raw) as Array<{ message?: string }>;
      if (Array.isArray(issues) && issues[0]?.message) return issues[0].message;
    } catch {
      /* not a zod issue array — fall through to the plain message */
    }
    return raw || 'Something went wrong. Please try again.';
  }
  return 'Could not reach the server. Please try again.';
}

function isNotFound(err: unknown): boolean {
  return err instanceof TRPCClientError && err.data?.code === 'NOT_FOUND';
}

/** The name is already taken in this business — with the app that holds it, so the screen
 *  can offer the way to it. */
export interface AppNameConflict {
  readonly appId: string | null;
  readonly message: string;
}

/** Classify an `apps.create` refusal. A CONFLICT is the taken-name case; anything else is
 *  a real error and returns null. */
export function appNameConflictOf(err: unknown): AppNameConflict | null {
  if (err instanceof TRPCClientError && err.data?.code === 'CONFLICT') {
    const m = /a_[A-Za-z0-9_-]+/.exec(err.message ?? '');
    return { appId: m ? m[0] : null, message: err.message ?? '' };
  }
  return null;
}

// ── the apps of one business (project page) ──────────────────────────────────────

export type AppsListStatus = 'loading' | 'ready' | 'error';

export interface UseProjectApps {
  readonly apps: App[];
  readonly status: AppsListStatus;
  readonly error: string | null;
  reload: () => Promise<void>;
  /** Name a new app and prepend it (it is the newest → belongs at the front). */
  create: (name: string) => Promise<App>;
}

export function useProjectApps(projectId: string): UseProjectApps {
  const [apps, setApps] = useState<App[]>([]);
  const [status, setStatus] = useState<AppsListStatus>('loading');
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      setApps(await client.apps.list.query({ projectId }));
      setStatus('ready');
    } catch (err) {
      setError(appErrorMessage(err));
      setStatus('error');
    }
  }, [projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const create = useCallback(
    async (name: string) => {
      const created = await client.apps.create.mutate({ projectId, name });
      setApps((prev) => [created, ...prev]);
      return created;
    },
    [projectId],
  );

  return { apps, status, error, reload, create };
}

// ── one app (the app page) ───────────────────────────────────────────────────────

export type AppLoadStatus = 'loading' | 'ready' | 'not-found' | 'error';

export interface UseApp {
  readonly app: App | null;
  /** The CURRENT name of the business this app belongs to (for the heading + breadcrumb). */
  readonly projectName: string | null;
  readonly status: AppLoadStatus;
  readonly error: string | null;
  reload: () => Promise<void>;
}

export function useApp(id: string): UseApp {
  const [app, setApp] = useState<App | null>(null);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [status, setStatus] = useState<AppLoadStatus>('loading');
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      const read = await client.apps.get.query({ id });
      setApp(read.app);
      setProjectName(read.projectName);
      setStatus('ready');
    } catch (err) {
      if (isNotFound(err)) {
        setApp(null);
        setProjectName(null);
        setStatus('not-found');
      } else {
        setError(appErrorMessage(err));
        setStatus('error');
      }
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { app, projectName, status, error, reload };
}
